import { differenceInSeconds, differenceInMinutes, parseISO } from "date-fns";

export const CANONICAL_STEPS = ["pending", "in_progress", "picked_up", "delivered"];

const TERMINAL_ALT_NAMES = ["cancelled", "failed"];

// Per-stage color, shared by the table's compact timeline and the modal's
// full timeline so a status always reads the same color everywhere.
// NOTE: this project's tailwind.config.js redefines "blue"/"red"/"green" as
// flat single-shade colors (not full 50-900 scales), so shade-suffixed
// classes like `bg-blue-500` silently don't exist — use the flat `bg-blue`
// token instead, or a shade family the config doesn't override (e.g. amber).
export const STEP_COLORS = {
  pending: "bg-amber-400",
  in_progress: "bg-blue",
  picked_up: "bg-blue",
  delivered: "bg-primaryGreen",
};

export const colorForStep = (name) => STEP_COLORS[name] || "bg-primaryGreen";

/**
 * Computes the canonical step state (reached/current/delta) from an order's
 * raw status history, shared by the table's compact timeline and the modal's
 * full timeline so they never drift out of sync.
 */
export function buildOrderStatusSteps(statuses = [], currentStatus) {
  const sorted = [...(statuses || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );
  const statusMap = new Map(sorted.map((s) => [s.name, s]));

  const isTerminalAlt =
    TERMINAL_ALT_NAMES.some((name) => statusMap.has(name)) ||
    TERMINAL_ALT_NAMES.includes(currentStatus);

  if (isTerminalAlt) {
    return { isTerminalAlt: true, steps: sorted, totalDeliveryMinutes: null };
  }

  const currentIndex = CANONICAL_STEPS.indexOf(currentStatus);

  let previousReachedTime = null;
  const steps = CANONICAL_STEPS.map((name, index) => {
    const entry = statusMap.get(name);
    // A step counts as reached either because we have a logged timestamp for
    // it, or because the order's current position has already passed it —
    // the latter keeps the progress line filled through any gaps in the
    // status history instead of leaving a false "not reached" segment.
    const reached = !!entry || (currentIndex >= 0 && index <= currentIndex);
    const time = entry?.createdAt ?? null;
    const deltaSeconds =
      time && previousReachedTime
        ? differenceInSeconds(parseISO(time), previousReachedTime)
        : null;
    if (time) previousReachedTime = parseISO(time);
    // The final step (delivered) is terminal, not "in progress" — once
    // reached it should read as done (checkmark), never as the pulsing
    // current indicator.
    const isCurrent = index === currentIndex && index < CANONICAL_STEPS.length - 1;
    return { name, reached, isCurrent, time, deltaSeconds };
  });

  const totalDeliveryMinutes =
    statusMap.has("picked_up") && statusMap.has("delivered")
      ? differenceInMinutes(
          parseISO(statusMap.get("delivered").createdAt),
          parseISO(statusMap.get("picked_up").createdAt),
        )
      : null;

  return { isTerminalAlt: false, steps, totalDeliveryMinutes };
}

export function humanizeDelta(seconds) {
  if (seconds == null) return null;
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
}
